import * as XLSX from "xlsx";
import Papa from "papaparse";
import { db } from "../firebase";
import { doc, setDoc } from "firebase/firestore";

/**
 * Parses an Excel or CSV file and maps rows to Firestore `hsCodes` collection.
 * Required columns: "HS Code", "Description", "Duty Rate", "GST"
 */
export async function parseAndUploadHSCodes(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                let rows = [];

                // Handle CSV
                if (file.name.endsWith(".csv")) {
                    const text = e.target.result;
                    const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
                    rows = parsed.data;
                }
                // Handle Excel
                else if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    rows = XLSX.utils.sheet_to_json(worksheet);
                } else {
                    throw new Error("Unsupported file format. Please upload .csv or .xlsx.");
                }

                let uploadedCount = 0;
                let errors = [];

                // Map and upload rows to Firestore
                for (const [index, row] of rows.entries()) {
                    // Normalize header keys by lowercasing and removing spaces/special chars
                    const normalizedRow = {};
                    for (let key in row) {
                        const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
                        normalizedRow[cleanKey] = row[key];
                    }

                    const rawCode = normalizedRow["hscode"] || normalizedRow["hs"] || normalizedRow["code"];
                    const desc = normalizedRow["description"] || normalizedRow["desc"];
                    const dutyRate = normalizedRow["dutyrate"] || normalizedRow["duty"];
                    let gstRate = normalizedRow["gst"] || normalizedRow["gstrate"];

                    if (!rawCode) {
                        errors.push(`Row ${index + 2}: Missing HS Code.`);
                        continue;
                    }

                    // Strip formatting characters from HS codes (e.g., '9503.00.00' -> '95030000')
                    const cleanCode = String(rawCode).replace(/[^0-9]/g, "");

                    if (!cleanCode) {
                        errors.push(`Row ${index + 2}: Invalid HS Code format (${rawCode}).`);
                        continue;
                    }

                    const payload = {
                        description: desc || "No description provided",
                        dutyRate: parseFloat(dutyRate) || 0,
                        gstRate: parseFloat(gstRate) || 10,  // default GST 10%
                        updatedAt: new Date().toISOString()
                    };

                    const docRef = doc(db, "hsCodes", cleanCode);
                    await setDoc(docRef, payload, { merge: true });
                    uploadedCount++;
                }

                resolve({ success: true, count: uploadedCount, errors });

            } catch (err) {
                console.error("Parse Error:", err);
                reject(err);
            }
        };

        reader.onerror = (err) => reject(err);

        if (file.name.endsWith(".csv")) {
            reader.readAsText(file);
        } else {
            reader.readAsArrayBuffer(file);
        }
    });
}
